import React from 'react'
import { ScanEventNode } from '../../gql/graphql'
import { formatShortRelative } from '../../utils/date'
import StatusPill from '../../components/Badges/StatusPill'
import { getBadgeColor, getTimeAgoBadgeColor } from './ScanHistoryTable.util'
import EllipsisDropdownMenu from '../../components/Menu/EllipsisDropdownMenu'

export type ScanEventForScanHistory = Pick<
  ScanEventNode,
  'id' | 'bag' | 'createdAt' | 'rawInputData'
>

interface ScanHistoryTableProps {
  events: ScanEventForScanHistory[]
  onDelete: (id: string) => void
  onRetire: (id: string) => void
}

export const ScanHistoryTable: React.FC<ScanHistoryTableProps> = ({
  events,
  onDelete,
  onRetire,
}) => {
  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            History
          </h1>
        </div>
      </div>
      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0"
                  >
                    ID
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Cycles
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Last scan
                  </th>
                  <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-0">
                    <span className="sr-only">Delete</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {events.map(event => (
                  <tr key={event.id}>
                    <td
                      className="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-500 sm:pl-0 md:
                    w-[100px]"
                    >
                      {event.bag
                        ? event.bag.humanReadableId ?? event.bag.bagId
                        : event.rawInputData}
                    </td>
                    <td className={'text-sm text-gray-500 leading-5 px-3'}>
                      <StatusPill color={getBadgeColor(event.bag?.cycles ?? 0)}>
                        {event.bag === null || event.bag === undefined
                          ? 'N/A'
                          : (event.bag.cycles ?? 0) > 200
                            ? '200+'
                            : event.bag.cycles}
                      </StatusPill>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      <StatusPill
                        color={getTimeAgoBadgeColor(new Date(event.createdAt))}
                      >
                        {formatShortRelative(
                          new Date(event.createdAt),
                          new Date()
                        )}
                      </StatusPill>
                    </td>
                    <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                      <EllipsisDropdownMenu
                        items={[
                          {
                            content: 'Delete scan',
                            className:
                              'text-gray-500 hover:text-gray-900 cursor-pointer',
                            onClick: () => onDelete(event.id),
                          },
                          {
                            content: 'Retire parcel',
                            className:
                              'text-red-500 hover:bg-red-500 hover:text-red-100 cursor-pointer',
                            onClick: () => {
                              if (event.bag) {
                                onRetire(event.bag.id)
                              }
                            },
                            show: event.bag !== null && event.bag !== undefined,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
